import React, { useEffect, useState } from "react";
import { CubeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";
import { formatCurrencyVND } from "../../utils/helps";

// Animation
const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

// Modal chi tiết sản phẩm
const ProductDetailModal = ({ slug, onClose }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    ApiCustom.get(`/product-show/${slug}`)
      .then((res) => setProduct(res.data?.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (!slug) return null;
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white rounded-2xl px-10 py-8 shadow-xl text-lg">
          Đang tải thông tin sản phẩm...
        </div>
      </div>
    );
  }
  if (!product) return null;

  // Trạng thái badge style newApple
  let statusClass = "bg-gray-100 text-gray-800 border";
  let statusText = "Hiện có";
  if (product.status === "out_of_stock") {
    statusClass = "bg-yellow-50 text-yellow-700 border border-yellow-100";
    statusText = "Hết hàng";
  } else if (product.status !== "available") {
    statusClass = "bg-red-50 text-red-600 border border-red-100";
    statusText = "Ngưng cung cấp";
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
      <div
        className="relative bg-white w-full max-w-2xl mx-2 rounded-2xl p-7 shadow-xl overflow-y-auto"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        {/* Ảnh và tên/mã */}
        <div className="flex flex-col sm:flex-row gap-7 mb-4">
          <div className="flex-shrink-0 flex justify-center items-center">
            <div className="w-40 h-40 rounded-xl overflow-hidden border bg-gray-50 flex items-center justify-center shadow-sm">
              {product.urlImage ? (
                <img
                  src={product.urlImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gray-300">No image</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <div className="text-2xl font-bold ">{product.name}</div>
            <div className="text-gray-500 text-sm">
              <span className="font-medium text-gray-700">Mã sản phẩm: </span>
              <span>{product.productCode || "---"}</span>
            </div>
            <div className="text-gray-500 text-sm">
              <span className="font-medium text-gray-700">Slug: </span>
              <span>{product.slug}</span>
            </div>
            <div>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusClass}`}
              >
                {statusText}
              </span>
            </div>
          </div>
        </div>
        {/* Mô tả */}
        {product.description && (
          <div className="mb-5 text-[15px]">
            <div className="font-semibold text-gray-700 mb-1">Mô tả</div>
            <div
              className="text-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          </div>
        )}
        {/* Grid thuộc tính */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[15px] mb-3">
          <div>
            <div>
              <span className="font-semibold text-gray-700">Đơn vị: </span>
              <span className="text-gray-800">
                {product.company?.companyName || "---"}
              </span>
            </div>

            {product.isContactPrice === true ? (
              <div>
                <span className="font-semibold text-gray-700">Giá: </span>
                <span>Liên hệ</span>
              </div>
            ) : (
              <>
                <div>
                  <span className="font-semibold text-gray-700">Giá gốc: </span>
                  <span>{formatCurrencyVND(product.price)}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">
                    Giá khuyến mãi:{" "}
                  </span>
                  <span>
                    {product.discountPrice &&
                    Number(product.discountPrice) !== 0
                      ? formatCurrencyVND(product.discountPrice)
                      : "---"}
                  </span>
                </div>
              </>
            )}
          </div>

          <div>
            <div>
              <span className="font-semibold text-gray-700">Danh mục: </span>
              <span className="text-gray-800">
                {product.category?.name || "---"}
              </span>
            </div>

            {product.isContactPrice !== true && (
              <div>
                <span className="font-semibold text-gray-700">
                  Đơn vị tính:{" "}
                </span>
                <span>{product.unit || "---"}</span>
              </div>
            )}

            <div>
              <span className="font-semibold text-gray-700">Quà tặng: </span>
              <span>{product.gift || "---"}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                Điều kiện quà:{" "}
              </span>
              <span>
                {product.giftCondition && Number(product.giftCondition) > 0
                  ? formatCurrencyVND(product.giftCondition)
                  : "---"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductTotalCard = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);

  useEffect(() => {
    setLoading(true);
    ApiCustom.get("/product-show")
      .then((res) => {
        const all = res.data?.data?.product || [];
        const approved = all.filter((p) => p?.approveStatus === "approved");
        setProducts(approved);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  // Table header (icon nhỏ, không màu mè, status có màu nhẹ)
  const headers = [
    {
      key: "urlImage",
      label: "Hình ảnh",
      render: (item) => (
        <div className="w-12 h-12 overflow-hidden rounded bg-gray-50 flex items-center justify-center">
          {item.urlImage ? (
            <img
              src={item.urlImage}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-gray-300">No image</span>
          )}
        </div>
      ),
    },
    {
      key: "name",
      label: "Tên sản phẩm",
      render: (item) => (
        <span className="font-medium text-gray-900">{item.name}</span>
      ),
    },
    {
      key: "company",
      label: "Đơn vị",
      render: (item) => (
        <span className="text-gray-700">{item.company?.companyName}</span>
      ),
    },
    {
      key: "price",
      label: "Giá tiền",
      render: (item) => (
        <span>
          {item.isContactPrice === true
            ? "Liên hệ"
            : formatCurrencyVND(item.price)}
        </span>
      ),
    },
    // {
    //   key: "status",
    //   label: "Trạng thái",
    //   render: (item) => {
    //     let style = "bg-gray-100 text-gray-800 border";
    //     let text = "Hiện có";
    //     if (item.status === "out_of_stock") {
    //       style = "bg-yellow-50 text-yellow-700 border border-yellow-100";
    //       text = "Hết hàng";
    //     } else if (item.status !== "available") {
    //       style = "bg-red-50 text-red-600 border border-red-100";
    //       text = "Ngưng cung cấp";
    //     }
    //     return (
    //       <span
    //         className={`px-3 py-1 rounded-full text-xs font-medium border ${style}`}
    //       >
    //         {text}
    //       </span>
    //     );
    //   },
    // },
  ];

  return (
    <div className="w-full">
      {/* Card: ngoài sang, icon màu nhấn */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="cursor-pointer transition-all p-5 rounded-2xl bg-white border border-fuchsia-100 shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-col items-center text-center">
          <div className="p-3 mb-3 bg-fuchsia-100 rounded-full flex items-center justify-center">
            <CubeIcon className="h-6 w-6 text-fuchsia-600" />
          </div>
          <div>
            <p className="text-black text-lg mb-1">Tổng số sản phẩm</p>
            <p className="text-3xl font-bold ">
              {loading ? "..." : products.length}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modal danh sách sản phẩm */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-7xl max-h-[90vh] mx-2 rounded-2xl p-6 shadow-xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <div className="mb-4">
                <h2 className="text-xl font-semibold ">Danh sách sản phẩm</h2>
              </div>
              {loading ? (
                <div className="text-center py-6 text-gray-400">
                  Đang tải...
                </div>
              ) : (
                <CustomTableComponent
                  headers={headers}
                  data={products}
                  onRowClick={(item) => setSelectedSlug(item.slug)}
                  className=""
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết sản phẩm */}
      <AnimatePresence>
        {selectedSlug && (
          <ProductDetailModal
            slug={selectedSlug}
            onClose={() => setSelectedSlug(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductTotalCard;
